import { useState, useEffect } from "react";
import { Box, Typography, Card, CardContent, Avatar, TextField, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "@context/NotificationContext";
import { GoogleLoginButton } from "@components/GoogleLoginButton";
import { ConfirmDialog } from "@components/ConfirmDialog";
import { PageContainer } from "@components/PageContainer";
import { AppMark } from "@components/AppMark";
import api from "@api/axios";
import { EMPTY_CELL_PLACEHOLDER } from "@/constants/app";

export default function Account() {
  const { t } = useTranslation();
  const { user, isAuthenticated, loading, deleteAccount } = useAuth();
  const { notify } = useNotification();
  const [displayName, setDisplayName] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (user) {
      setDisplayName(user.displayName || "");
    }
  }, [user]);

  if (loading) return null;

  if (!isAuthenticated) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          gap: 3,
        }}
      >
        <AppMark size={64} fontSize={24} borderRadius={3} sx={{ mb: 1 }} />
        <Typography
          variant="h5"
          sx={{
            fontWeight: "bold",
          }}
        >
          {t("app.name")}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            textAlign: "center",
            maxWidth: 400,
          }}
        >
          {t("account.signInSubtitle")}
        </Typography>
        <GoogleLoginButton />
      </Box>
    );
  }

  const handleSave = async () => {
    try {
      await api.patch("/users/me", { displayName });
      notify(t("account.updated"), "success");
    } catch {
      notify(t("account.profileUpdateFailed"), "error");
    }
  };

  const handleDelete = async () => {
    setConfirmOpen(false);
    try {
      await deleteAccount();
    } catch {
      notify(t("account.deleteFailed"), "error");
    }
  };

  return (
    <PageContainer sx={{ maxWidth: 600 }}>
      <Typography
        variant="h5"
        sx={{
          fontWeight: "bold",
          mb: 3,
        }}
      >
        {t("account.title")}
      </Typography>
      <Card>
        <CardContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar src={user?.avatarUrl || undefined} sx={{ width: 64, height: 64 }}>
              {user?.email?.[0]?.toUpperCase()}
            </Avatar>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                }}
              >
                {user?.displayName || user?.email}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                {user?.email}
              </Typography>
            </Box>
          </Box>

          <TextField
            label={t("account.displayName")}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            size="small"
            fullWidth
          />

          <TextField
            label={t("account.email")}
            value={user?.email || ""}
            size="small"
            fullWidth
            disabled
          />

          <Box>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                display: "block",
                mb: 0.5,
              }}
            >
              {t("account.memberSince")}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 500,
              }}
            >
              {user?.createdAt
                ? new Date(user.createdAt).toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                : EMPTY_CELL_PLACEHOLDER}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
            <Button variant="contained" color="secondary" onClick={handleSave}>
              {t("common.save")}
            </Button>
            <Button variant="outlined" color="error" onClick={() => setConfirmOpen(true)}>
              {t("account.deleteAccount")}
            </Button>
          </Box>
        </CardContent>
      </Card>
      <ConfirmDialog
        open={confirmOpen}
        title={t("account.deleteAccount")}
        message={t("account.deleteConfirm")}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </PageContainer>
  );
}
